/**
 * Presupuesto del checkout — PURO, la misma función en front (desglose) y back (cobro).
 *
 * A7 (plan 12): el precio está en la moneda del VENDEDOR; se cobra en la del COMPRADOR con el tipo
 * del momento (`fx.rates`, unidades por 1 EUR, del BCE vía Frankfurter como la 1.0 fx.js) y ese tipo
 * se congela en `orders.seller_amount.fx_rate`. Las tarifas fijas y la estimación de envío (EUR) se
 * convierten con la tabla fija `settings/fees.fee_fx_rates`, igual que `estimateProtection`, para
 * que la ficha y el checkout coincidan.
 */
import {
  CURRENCY_MINOR_DIGITS,
  money,
  type Currency,
  type Money,
} from '../firestore/primitives.js';
import type { FeesSettings } from '../firestore/settings.js';
import type { FulfillmentMode, OrderAmounts } from '../firestore/orders.js';
import { DEFAULT_OPTIONAL_PROTECTION_PERCENT, quoteOrder, roundHalfUp } from '../money/fees.js';
import { feeInCurrency } from '../money/protection.js';
import { estimateShippingEur, gramsToTier } from '../shipping/tiers.js';

export interface FxRates {
  /** Unidades de cada moneda por 1 EUR (EUR = 1). */
  rates: Readonly<Partial<Record<Currency, number>>>;
  /** `ecb` (Frankfurter), `fallback` (tabla fija de settings/fees) … se guarda en el pedido. */
  source: string;
}

export class FxRateMissingError extends TypeError {
  constructor(public readonly currency: Currency) {
    super(`fx: sin tipo de cambio para ${currency}`);
    this.name = 'FxRateMissingError';
  }
}

/** Tipo `from` → `to` (cuántas unidades de `to` vale 1 unidad de `from`). */
export function fxRate(from: Currency, to: Currency, fx: FxRates): number {
  if (from === to) return 1;
  const a = from === 'EUR' ? 1 : fx.rates[from];
  const b = to === 'EUR' ? 1 : fx.rates[to];
  if (a === undefined || !(a > 0)) throw new FxRateMissingError(from);
  if (b === undefined || !(b > 0)) throw new FxRateMissingError(to);
  return b / a;
}

/** Convierte con un tipo ya calculado; redondeo half-up a la unidad menor de `to`. */
export function convertWithRate(src: Money, to: Currency, rate: number): Money {
  if (src.currency === to) return src;
  const units = src.amount / 10 ** CURRENCY_MINOR_DIGITS[src.currency];
  return money(roundHalfUp(units * rate * 10 ** CURRENCY_MINOR_DIGITS[to]), to);
}

export type CheckoutFees = Pick<
  FeesSettings,
  | 'protection_fee_percent'
  | 'protection_fee_fixed'
  | 'transport_surcharge'
  | 'handover_fee'
  | 'protection_fee_waived_above'
  | 'wallet_payment_waives_fixed'
  | 'club_tiers'
  | 'fee_fx_rates'
  | 'optional_protection_percent'
>;

export interface CheckoutQuoteInput {
  /** Precio del anuncio en la moneda del vendedor. */
  price: Money;
  buyer_currency: Currency;
  fulfillment_mode: FulfillmentMode;
  /** Gramos de cotización (productQuotedGrams); se ignora en handover. */
  quoted_grams: number;
  /** País del vendedor y del destino: decide tabla nacional/internacional. */
  domestic: boolean;
  /** `free_shipping` del anuncio: el comprador no paga el envío (lo asume el vendedor, 1.0). */
  free_shipping: boolean;
  /** Parte pagada con saldo del wallet (C1), en la moneda del comprador; 0 por ahora. */
  wallet_portion?: Money;
  /** Precio real del transportista elegido (sección 8), ya en la moneda del comprador; sin él, la tabla de tramos. */
  shipping_override?: Money;
  completed_purchases: number;
  referral_first_purchase?: boolean;
  /** B5: tope del envío para el comprador (ya en su moneda); nuvvu asume la diferencia. */
  shipping_cap?: Money;
  /** L4: protección opcional elegida por el comprador. */
  optional_protection?: boolean;
  fees: CheckoutFees;
  fx: FxRates;
}

export interface CheckoutQuote {
  amounts: OrderAmounts;
  seller_amount: { item: Money; fx_rate: number; fx_source: string };
  /** Envío estimado base (antes de `free_shipping`), para enseñar «envío incluido». */
  shipping_estimate: Money;
  shipping_tier: number | null;
  /** L4: precio de la protección opcional si el artículo supera el umbral (null si no aplica). */
  optional_protection: { price: Money; selected: boolean } | null;
  /** B5: lo que asume nuvvu por la campaña (0 si no aplica). */
  campaign_subsidy: Money;
}

export function quoteCheckout(input: CheckoutQuoteInput): CheckoutQuote {
  const cur = input.buyer_currency;
  const rate = fxRate(input.price.currency, cur, input.fx);
  const item = convertWithRate(input.price, cur, rate);
  const fx = input.fees.fee_fx_rates;
  const handover = input.fulfillment_mode === 'handover';

  const shippingEur = handover
    ? money(0, 'EUR')
    : estimateShippingEur(input.quoted_grams, input.domestic);
  const shippingEstimate = handover
    ? money(0, cur)
    : (input.shipping_override ?? feeInCurrency(shippingEur, cur, fx));
  if (input.shipping_override && input.shipping_override.currency !== cur)
    throw new TypeError('quoteCheckout: shipping_override en otra moneda');
  const beforeCap = input.free_shipping ? money(0, cur) : shippingEstimate;
  const capped =
    input.shipping_cap && input.shipping_cap.amount < beforeCap.amount
      ? money(input.shipping_cap.amount, cur)
      : beforeCap;
  const campaign_subsidy = money(beforeCap.amount - capped.amount, cur);
  const shipping = capped;

  const amounts = quoteOrder(
    {
      item,
      shipping,
      fulfillment_mode: input.fulfillment_mode,
      wallet_portion: input.wallet_portion ?? money(0, cur),
      completed_purchases: input.completed_purchases,
      ...(input.referral_first_purchase ? { referral_first_purchase: true } : {}),
      ...(input.optional_protection ? { optional_protection: true } : {}),
    },
    input.fees,
    {
      protection_fixed: feeInCurrency(input.fees.protection_fee_fixed, cur, fx),
      transport_surcharge: feeInCurrency(input.fees.transport_surcharge, cur, fx),
      handover_fee: feeInCurrency(input.fees.handover_fee, cur, fx),
      protection_waived_above:
        input.fees.protection_fee_waived_above === null
          ? null
          : feeInCurrency(input.fees.protection_fee_waived_above, cur, fx),
    },
  );
  const threshold =
    input.fees.protection_fee_waived_above === null
      ? null
      : feeInCurrency(input.fees.protection_fee_waived_above, cur, fx);
  const optionalPercent =
    input.fees.optional_protection_percent ?? DEFAULT_OPTIONAL_PROTECTION_PERCENT;
  const optional_protection =
    threshold !== null && item.amount >= threshold.amount && input.referral_first_purchase !== true
      ? {
          price: money(roundHalfUp((item.amount * optionalPercent) / 100), cur),
          selected: input.optional_protection === true,
        }
      : null;
  return {
    amounts,
    optional_protection,
    seller_amount: { item: input.price, fx_rate: rate, fx_source: input.fx.source },
    shipping_estimate: shippingEstimate,
    shipping_tier: handover ? null : gramsToTier(input.quoted_grams),
    campaign_subsidy,
  };
}

/** Minutos que dura la reserva del artículo mientras el comprador paga (1.0 BUY_NOW_PAYMENT_MINUTES). */
export const CHECKOUT_RESERVATION_MINUTES = 30;
/** Stripe exige ≥ 30 min de vida a una sesión de Checkout; se le da margen. */
export const CHECKOUT_SESSION_MINUTES = 35;

// ---------- C7: entrega en mano con código / QR ----------

/** 6 caracteres sin ambigüedad (ni 0/O ni 1/I): el vendedor lo enseña, el comprador lo teclea o escanea. */
export const HANDOVER_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const HANDOVER_CODE_RE = /^[A-HJ-NP-Z2-9]{6}$/;

export function normalizeHandoverCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/[\s-]/g, '');
}

/** Lo que va dentro del QR del vendedor; la app lo abre y rellena el código. */
export function handoverQrPayload(orderId: string, code: string): string {
  return `nuvvu:handover:${orderId}:${code}`;
}

export function parseHandoverQr(text: string): { orderId: string; code: string } | null {
  const m = /^nuvvu:handover:([A-Za-z0-9]+):([A-Z2-9]{6})$/.exec(text.trim());
  return m?.[1] && m[2] ? { orderId: m[1], code: m[2] } : null;
}

// ---------- ofertas (1.0 MAX_OFFER_DISCOUNT_RATE: la oferta mínima es el 60 % del precio) ----------

/** Límites de una oferta sobre el precio de lista (unidad menor): [min, max = precio - 1 unidad]. */
export function offerBounds(price: Money, maxDiscountPercent: number): { min: Money; max: Money } {
  const min = roundHalfUp((price.amount * (100 - maxDiscountPercent)) / 100);
  return {
    min: money(Math.max(1, min), price.currency),
    max: money(Math.max(1, price.amount - 1), price.currency),
  };
}

export function offerProblem(
  offer: Money,
  price: Money,
  maxDiscountPercent: number,
): 'currency' | 'too_low' | 'too_high' | null {
  if (offer.currency !== price.currency) return 'currency';
  const { min, max } = offerBounds(price, maxDiscountPercent);
  if (offer.amount < min.amount) return 'too_low';
  if (offer.amount > max.amount) return 'too_high';
  return null;
}

/** Las ofertas y contraofertas caducan a las 24 h (sección 7). */
export const OFFER_TTL_HOURS = 24;

// ---------- lotes (D5): varios artículos del mismo vendedor, un envío, descuento por tramos ----------

/** Porcentaje que toca a `count` artículos: el mayor tramo alcanzado (2 → 3 → 5), acotado al tope. */
export function bundleDiscountPercent(
  count: number,
  tiers: Readonly<Partial<Record<'2' | '3' | '5', number>>> | undefined,
  maxPercent: number,
): number {
  if (!tiers || count < 2) return 0;
  let pct = 0;
  for (const t of ['2', '3', '5'] as const) {
    const v = tiers[t];
    if (count >= Number(t) && typeof v === 'number' && v > pct) pct = v;
  }
  return Math.min(Math.max(0, Math.round(pct)), maxPercent);
}

export interface BundleQuote {
  subtotal: Money;
  discount_percent: number;
  discount: Money;
  /** Subtotal menos descuento: es el «precio del artículo» que entra en quoteOrder. */
  total: Money;
}

/** Suma de precios (misma moneda) y descuento por lote. */
export function quoteBundle(prices: readonly Money[], discountPercent: number): BundleQuote {
  const first = prices[0];
  if (!first) throw new TypeError('quoteBundle: sin artículos');
  const cur = first.currency;
  for (const p of prices)
    if (p.currency !== cur) throw new TypeError(`quoteBundle: moneda ${p.currency} ≠ ${cur}`);
  const subtotal = money(
    prices.reduce((s, p) => s + p.amount, 0),
    cur,
  );
  const discount = money(roundHalfUp((subtotal.amount * discountPercent) / 100), cur);
  return {
    subtotal,
    discount_percent: discountPercent,
    discount,
    total: money(subtotal.amount - discount.amount, cur),
  };
}

/** Artículos máximos por lote (1.0: sin tope práctico; la 2.0 limita el envío único). */
export const MAX_BUNDLE_ITEMS = 20;
